import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { designSystem } from '@yola/ws-ui';
import useEventListener from 'src/js/modules/utils/custom-hooks/use-event-listener';
import dropdownEvents from 'src/js/modules/common/constants/dropdown-events';

const { Dropdown, DropdownTrigger, DropdownContent, ActionList, ActionListItem, Icon, IconButton } =
  designSystem;

// ws-ui `Modal` sits at z-index 1000, while `Dropdown` defaults to 999, so the
// dropdown content would render behind the settings dialog.
const DROPDOWN_Z_INDEX = 1001;

const DeviceModeDropdown = ({ modeOptions, activeMode, onChange, onShow }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleShow = useCallback(() => {
    setIsOpen(true);
    onShow();
  }, [onShow]);
  const handleHide = useCallback(() => setIsOpen(false), []);
  const handleOptionClick = useCallback(
    (mode) => {
      onChange(mode);
      Dropdown.hide();
    },
    [onChange]
  );

  useEventListener(dropdownEvents.HIDE, handleHide);

  if (!activeMode) return null;

  const activeOption = modeOptions.find((option) => option.mode === activeMode);
  const caretClassName = classNames('ws-device-mode-dropdown__caret', {
    'ws-device-mode-dropdown__caret--open': isOpen,
  });

  return (
    <Dropdown
      placement="bottom-right"
      zIndex={DROPDOWN_Z_INDEX}
      onShow={handleShow}
      onHide={handleHide}
    >
      <DropdownTrigger>
        <div className="ws-device-mode-dropdown__trigger">
          <span className={caretClassName}>
            <Icon glyph="down-small-for-nav-item" size={24} />
          </span>
          <IconButton glyph={activeOption?.glyph} transparent />
        </div>
      </DropdownTrigger>
      <DropdownContent>
        <ActionList>
          {modeOptions.map(({ mode, glyph, label }) => (
            <ActionListItem
              key={mode}
              title={label}
              iconGlyph={glyph}
              iconPlacement="left"
              selected={activeMode === mode}
              onClick={() => {
                handleOptionClick(mode);
                handleHide();
              }}
              style={{ minWidth: '200px' }}
            />
          ))}
        </ActionList>
      </DropdownContent>
    </Dropdown>
  );
};

DeviceModeDropdown.propTypes = {
  modeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      mode: PropTypes.string.isRequired,
      glyph: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  activeMode: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onShow: PropTypes.func.isRequired,
};

export default DeviceModeDropdown;
